<template>
    <div>
        <VueDataTableV2
            ref="datatable"
            :columns="tableColumns"
            :api-url="apiUrl"
            :api-headers="apiHeaders"
            :api-params="apiParams"
            :show-selection="false"
            :show-column-toggle-button="true"
            :show-bulk-actions="false"
            @data-loaded="onDataLoaded"
            :local-storage-key="'billet-bo-v2'"
            @row-click="onRowClick"
            @error="onError"
        >
            <template #header-left> </template>

            <template #header-right>
                <button v-if="canImport" @click="openImportModal" class="btn btn-sm px-3 rounded-8 fw-medium btn-primary me-2">
                    <i class="fas fa-upload"></i>
                </button>
                <button @click="exportToExcel" class="btn btn-sm px-3 rounded-8 fw-medium btn-primary">
                    <i class="fas fa-download"></i>
                </button>
            </template>

            <!-- Status Slot -->
            <template #status="{ item }">
                <span :class="getStatusClass(item.status)">{{ getStatusLabel(item.status) }}</span>
            </template>

            <!-- Gifted By Slot -->
            <template #gifted_by="{ item }">
                <a v-if="item.from" class="m-1 fs-sm poppinsMedium" href="#" @click.prevent="openCustomerModal(item.from.id)">
                    {{ formatUser(item.from) }}
                </a>
                <span v-else>
                    {{
                        formatUser({
                            first_name: item.from_first_name,
                            last_name: item.from_last_name,
                            email: item.from_email,
                        })
                    }}
                </span>
            </template>

            <!-- Gifted To Slot -->
            <template #gifted_to="{ item }">
                <a v-if="item.to" class="m-1 fs-sm poppinsMedium" href="#" @click.prevent="openCustomerModal(item.to.id)">
                    {{ formatUser(item.to) }}
                </a>
                <span v-else>
                    {{
                        formatUser({
                            first_name: item.to_first_name,
                            last_name: item.to_last_name,
                            email: item.to_email,
                        })
                    }}
                </span>
            </template>

            <!-- Actions Slot -->
            <template #actions="{ item }">
                <div class="dropdown d-inline-block">
                    <button
                        type="button"
                        class="dropdown-button"
                        id="page-header-action-dropdown"
                        data-bs-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M5 10C3.9 10 3 10.9 3 12C3 13.1 3.9 14 5 14C6.1 14 7 13.1 7 12C7 10.9 6.1 10 5 10ZM19 10C17.9 10 17 10.9 17 12C17 13.1 17.9 14 19 14C20.1 14 21 13.1 21 12C21 10.9 20.1 10 19 10ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10Z"
                                fill="#94A3B8"
                            />
                        </svg>
                    </button>
                    <div
                        class="dropdown-menu dropdown-action dropdown-menu-end"
                        aria-labelledby="page-header-action-dropdown"
                        data-popper-placement="bottom-end"
                    >
                        <a class="btn btn-sm btn-action m-1" title="Modifier" :href="getEditUrl(item.id)"> Modifier </a>

                        <a
                            v-if="item.order_item"
                            class="btn btn-sm btn-action m-1"
                            :href="getInvoiceUrl(item.order_item.order.id)"
                        >
                            Générer facture
                        </a>

                        <a
                            v-if="item.is_blocked"
                            class="btn btn-sm btn-action m-1"
                            @click="setSelectedVoucher(item.id, 'enable')"
                            title="Réactiver"
                        >
                            Réactiver
                        </a>
                        <a
                            v-else
                            class="btn btn-sm btn-action m-1"
                            @click="setSelectedVoucher(item.id, 'disable')"
                            title="Désactiver"
                        >
                            Désactiver
                        </a>

                        <a class="btn btn-sm btn-action m-1" :href="getHistoryUrl(item.id)"> Historique </a>

                        <a class="btn btn-sm m-1 btn-action" :href="getSendUrl(item.id)"> Renvoyer le mail </a>

                        <a class="btn btn-sm m-1 btn-action" :href="getDownloadUrl(item.id)"> Télécharger </a>
                    </div>
                </div>
            </template>
        </VueDataTableV2>

        <!-- Toggle Voucher Modal Component -->
        <VoucherToggleModal
            modal-id="modal-toggle-billet"
            :title="modalTitle"
            :confirm-text="modalButtonText"
            cancel-text="Fermer"
            :loading="isLoading"
            :show="showModal"
            @confirm="confirmToggleStatus"
            @cancel="closeModal"
            @close="closeModal"
        />
    </div>
</template>

<script>
import VueDataTableV2 from '../../V2/datatable/VueDataTableV2.vue'
import VoucherToggleModal from './VoucherToggleModal.vue'

export default {
    name: 'BilletDatatableV2',
    components: {
        VueDataTableV2,
        VoucherToggleModal,
    },
    props: {
        canImport: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedVoucherId: null,
            selectedAction: null,
            isLoading: false,
            showModal: false,
            tableColumns: [
                {
                    key: 'code',
                    title: 'Code',
                    sortable: true,
                    search: { type: 'text' },
                },
                {
                    key: 'send_date',
                    title: "Date d'envoi",
                    sortable: true,
                    formatter: (value) => {
                        if (!value) return '-'
                        const date = typeof value === 'string' ? new Date(value) : value
                        if (isNaN(date.getTime())) return '-'
                        return date.toLocaleDateString('fr-FR')
                    },
                },
                {
                    key: 'date_end',
                    title: "Date d'expiration",
                    sortable: true,
                    formatter: (value) => {
                        if (!value) return '-'
                        const date = typeof value === 'string' ? new Date(value) : value
                        if (isNaN(date.getTime())) return '-'
                        return date.toLocaleDateString('fr-FR')
                    },
                },
                {
                    key: 'import_note',
                    title: "Note d'import",
                    sortable: false,
                },
                {
                    key: 'total_amount',
                    title: 'Montant total',
                    sortable: true,
                    formatter: (value) => {
                        return `${value} ${this.$symbolCurrency}`
                    },
                },
                {
                    key: 'available_amount',
                    title: 'Solde',
                    sortable: true,
                    formatter: (value) => {
                        return `${value} ${this.$symbolCurrency}`
                    },
                },
                {
                    key: 'status',
                    title: 'Statut',
                    sortable: false,
                    slot: 'status',
                    search: {
                        type: 'select',
                        placeholder: 'Tous les statuts',
                        options: [
                            { value: 'active', label: 'Active' },
                            { value: 'used', label: 'Utilisé' },
                            { value: 'expired', label: 'Perimé' },
                            { value: 'blocked', label: 'Bloqué' },
                            { value: 'partially_used', label: 'Utilisé partiellement' },
                        ],
                    },
                },
                {
                    key: 'gifted_by',
                    title: 'Offert par',
                    sortable: false,
                    slot: 'gifted_by',
                },
                {
                    key: 'gifted_to',
                    title: 'Offert à',
                    sortable: false,
                    slot: 'gifted_to',
                },
                {
                    key: 'items',
                    title: 'Contenu',
                    sortable: false,
                    formatter: (value) => {
                        if (!value || !Array.isArray(value) || value.length === 0) {
                            return 'Aucun objet lié.'
                        }
                        return value
                            .map((item) => {
                                if (item.item_id == -1) {
                                    return `${item.amount} ${this.$symbolCurrency}`
                                } else {
                                    return `${item.quantity || 1} x ${item.voucher_itemable?.name || 'Article'}`
                                }
                            })
                            .join(', ')
                    },
                },
                {
                    key: 'actions',
                    title: 'Actions',
                    sortable: false,
                    slot: 'actions',
                },
            ],
            apiHeaders: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }
    },
    computed: {
        apiUrl() {
            return `${window.location.origin}/back-office/billet_non_date-v2/ajax`
        },
        apiParams() {
            return {
                columns: this.tableColumns.map((col) => col.key),
            }
        },
        csrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        modalTitle() {
            return this.selectedAction === 'enable' ? 'Voulez-vous réactiver ce billet ?' : 'Voulez-vous désactiver ce billet ?'
        },
        modalButtonText() {
            return this.selectedAction === 'enable' ? 'Activer' : 'Désactiver'
        },
    },
    methods: {
        getStatusClass(status) {
            const classes = {
                active: 'badge bg-success text-white rounded-pill',
                blocked: 'badge bg-danger text-white rounded-pill',
                partially_used: 'badge bg-warning text-white rounded-pill',
                expired: 'badge bg-secondary text-white rounded-pill',
                used: 'badge bg-info text-white rounded-pill',
            }
            return classes[status] || 'badge bg-secondary text-white rounded-pill'
        },

        getStatusLabel(status) {
            const labels = {
                active: 'Actif',
                blocked: 'Bloqué',
                partially_used: 'Utilisé partiellement',
                expired: 'Perimé',
                used: 'Utilisé',
            }
            return labels[status] || status
        },

        formatUser(user) {
            const parts = []

            if (user.first_name) parts.push(user.first_name)
            if (user.last_name) parts.push(user.last_name)

            if (!parts.length && user.email) parts.push(user.email)

            if (parts.length && user.email && !parts.includes(`(${user.email})`)) {
                parts.push(`(${user.email})`)
            }

            if (!parts.length) return '-'

            return parts.join(' ')
        },

        openCustomerModal(customerId) {
            // Use the global function that exists in the page
            if (typeof window.setOrderToModal === 'function') {
                window.setOrderToModal(customerId)
                // Use jQuery to trigger Bootstrap modal
                if (window.jQuery) {
                    window.jQuery('#modal-customer').modal('show')
                }
            }
        },

        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },

        onRowClick(item) {
            console.log('Row clicked:', item)
        },

        onError(error) {
            console.error('Datatable error:', error)
            this.$toast.error(error.message || this.$t('common.data_load_error'))
        },

        handleAction(action, item) {
            if (action.method === 'GET') {
                window.location.href = action.url
            } else if (action.method === 'POST') {
                // Handle POST actions like disable/enable
                const form = document.createElement('form')
                form.method = 'POST'
                form.action = action.url
                form.style.display = 'none'

                // Add CSRF token
                const tokenInput = document.createElement('input')
                tokenInput.type = 'hidden'
                tokenInput.name = '_token'
                tokenInput.value = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                form.appendChild(tokenInput)

                // Add voucher parameter
                const voucherInput = document.createElement('input')
                voucherInput.type = 'hidden'
                voucherInput.name = 'voucher'
                voucherInput.value = item.id
                form.appendChild(voucherInput)

                document.body.appendChild(form)
                form.submit()
            }
        },

        getEditUrl(id) {
            return `/back-office/bons_cadeau/${id}/edit`
        },

        getInvoiceUrl(orderId) {
            return `/back-office/invoice/${orderId}`
        },

        getHistoryUrl(id) {
            return `/back-office/bons_cadeau/${id}/history`
        },

        getSendUrl(id) {
            return `/back-office/bons_cadeau/${id}/send_voucher`
        },

        getDownloadUrl(id) {
            return `/back-office/bons_cadeau-v2/${id}`
        },

        setSelectedVoucher(id, action) {
            this.selectedVoucherId = id
            this.selectedAction = action
            this.showModal = true
        },

        closeModal() {
            this.showModal = false
            this.selectedVoucherId = null
            this.selectedAction = null
        },

        updateRowStatus(voucherId, action) {
            // Find the row in the datatable data and update its is_blocked property
            if (this.$refs.datatable && this.$refs.datatable.data) {
                const rowIndex = this.$refs.datatable.data.findIndex((row) => row.id === voucherId)
                if (rowIndex !== -1) {
                    // Update the is_blocked property based on action
                    this.$refs.datatable.data[rowIndex].is_blocked = action === 'disable'
                    // Force Vue to detect the change if needed
                    this.$refs.datatable.$forceUpdate()
                }
            }
        },

        async confirmToggleStatus() {
            if (!this.selectedVoucherId || !this.selectedAction) return

            this.isLoading = true
            try {
                const response = await this.$http.post(
                    `/back-office/billet_non_date-v2/${this.selectedVoucherId}/toggle-status`,
                    {
                        action: this.selectedAction,
                        _token: this.csrfToken,
                    }
                )

                if (response.data.success) {
                    this.$toast.success(response.data.message)

                    // Update the row status manually without refreshing the page
                    this.updateRowStatus(this.selectedVoucherId, this.selectedAction)

                    // Close modal
                    this.closeModal()
                } else {
                    throw new Error(response.data.message || "Une erreur s'est produite")
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || error.message || "Une erreur s'est produite")
            } finally {
                this.isLoading = false
            }
        },

        openImportModal() {
            // Open the Dromo import modal
            if (typeof window.dromo !== 'undefined' && window.dromo.open) {
                window.dromo.open()
            } else {
                console.error('Dromo is not initialized')
            }
        },

        async exportToExcel() {
            try {
                // Get current filters from the datatable component
                const searchQuery = this.$refs.datatable?.searchQuery || ''
                const sortBy = this.$refs.datatable?.sortBy || ''
                const sortOrder = this.$refs.datatable?.sortOrder || 'desc'
                const columnSearches = this.$refs.datatable?.columnSearches || {}

                // Build query parameters
                const params = new URLSearchParams({
                    export: '1',
                    search: searchQuery,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    ...columnSearches,
                })

                // Create a download link element
                const link = document.createElement('a')
                link.href = `${this.apiUrl}?${params.toString()}`
                link.download = `billets_non_dates_${new Date().toISOString().split('T')[0]}.xlsx`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                this.$toast.success(this.$t('common.excel_download_success'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
    },
}
</script>

<style scoped>
.badge.bg-success {
    background-color: #33df6f !important;
}

.badge.bg-danger {
    background-color: #ff2424 !important;
}

.badge.bg-info {
    background-color: #0094fb96 !important;
}
</style>
